'use strict';

import { UntypedFormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { environment } from '../environments/environment';

// Values loaded from environment configuration
export const API_URL = environment.apiUrl;
export const SITEURL = environment.siteUrl;
export const CAPTCHA_SITE_KEY = environment.captchaSiteKey;
export const TinyMCE_KEY = environment.tinyMceKey;
export const APP_NAME = environment.appName;
export const LOGO = environment.logo;
export const TABLE_LENGTH = 10;
export const CURRENT_YEAR = new Date().getFullYear();

export function getServerErrorMessage(err: any) {
  if (err.status == 401) {
    return err?.message ?? 'Unauthorized Action';
  } else if (err.status == 403) {
    return err?.message ?? 'Failed to authenticate';
  } else if (err.status == 404) {
    return err?.message ?? 'Not found exception occured';
  } else if (err.status == 406) {
    return err?.message ?? 'Subscription Expired';
  } else {
    return 'Internal server error occured. Please try again later';
  }
}
export function calculateDiff(dateSent: any) {
  let currentDate = new Date();
  dateSent = new Date(dateSent);
  return Math.floor(
    (Date.UTC(dateSent.getFullYear(), dateSent.getMonth(), dateSent.getDate()) -
      Date.UTC(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate()
      )) /
      (1000 * 60 * 60 * 24)
  );
}
export function expiredIn(expiry: any) {
  var start = moment(new Date(), 'DD.MM.YYYY');
  var end = moment(new Date(expiry), 'DD.MM.YYYY');
  let value = end.diff(start, 'days');
  return value ?? 0;
}

export function getValidationMessage(result: any[]) {
  for (const key in result) {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      const element = result[key];
      if (element.message) {
        return element.message;
      }
    }
  }

  return 'Validation Error: Please check all the fields correctly';
}

export function scrollToQuery(query: any) {
  let $_errFormControl = document.querySelectorAll(query);
  if ($_errFormControl.length > 0) {
    const firstErr: Element = $_errFormControl[0];
    firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

export function resetPaginationOption() {
  return {
    hasNextPage: false,
    hasPrevPage: false,
    limit: TABLE_LENGTH,
    nextPage: null,
    page: 1,
    pagingCounter: 1,
    prevPage: null,
    totalDocs: 0,
    totalPages: 1,
  };
}

export function resetTableFilterOptions() {
  return {
    search_filter: '',
    searchkey: '',
    section_id: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  };
}

export function getPageNumber({
  index = <number>1,
  pageno = <number>1,
  tablelength = <number>TABLE_LENGTH,
} = {}) {
  return tablelength * (pageno - 1) + (index + 1);
}

export function disableButtonForTimeFrame(
  remaining: number,
  buttonid: string,
  defaultText: string = 'Resend'
) {
  let button = document.getElementById(buttonid);

  if (!button) {
    return false;
  }

  button.setAttribute('disabled', 'disabled');

  var m: any = Math.floor(remaining / 60);
  var s: any = remaining % 60;

  m = m < 10 ? '0' + m : m;
  s = s < 10 ? '0' + s : s;

  let buttonIndicatorLabel = button?.querySelector('span.indicator-label');
  if (buttonIndicatorLabel) {
    buttonIndicatorLabel.innerHTML =
      '<i class="fa fa-clock-o"></i>&nbsp;&nbsp;' + m + ':' + s;
    remaining -= 1;

    if (remaining >= 0) {
      setTimeout(function () {
        disableButtonForTimeFrame(remaining, buttonid);
      }, 1000);
      return;
    }

    buttonIndicatorLabel.innerHTML = defaultText;
    button.removeAttribute('disabled');
    return true;
  } else {
    return false;
  }
}

/**
 * ----------------------------------------
 * Form Control Global Functions
 * @param formGroup - Instance of FormGroup
 * ----------------------------------------
 * ----------------------------------------
 */

export function resetForm(formGroup: FormGroup) {
  formGroup.reset();
  for (const key in formGroup.controls) {
    if (Object.prototype.hasOwnProperty.call(formGroup.controls, key)) {
      const element = formGroup.controls[key];

      element.markAsUntouched();
      element.markAsPristine();
    }
  }
}

export function isFormValidationAvailable(
  formGroup: FormGroup,
  control: any,
  rules: any
) {
  const formControl: any = formGroup.get(control);
  if (formControl) {
    const validator =
      formControl.validator && formControl.validator(new UntypedFormControl());
    if (validator && validator[rules]) {
      return true;
    }
  }

  return false;
}

export function isInputValid(formGroup: FormGroup, control: any) {
  let valid: boolean = true;
  if (
    !['VALID', 'DISABLED'].includes(formGroup.controls[control].status) &&
    (formGroup.controls[control].touched || formGroup.controls[control].dirty)
  ) {
    valid = false;
  }

  return valid;
}

export function isInputRuleValid(
  formGroup: FormGroup,
  control: any,
  rule: any
) {
  let valid: boolean = true;

  if (rule instanceof Array) {
    rule.forEach((r) => {
      if (
        formGroup.controls[control].hasError(r) &&
        (formGroup.controls[control].touched ||
          formGroup.controls[control].dirty)
      ) {
        valid = false;
      }
    });
  } else {
    if (
      formGroup.controls[control].hasError(rule) &&
      (formGroup.controls[control].touched || formGroup.controls[control].dirty)
    ) {
      valid = false;
    }
  }

  return valid;
}

export function isInputRuleAvailable(
  formGroup: FormGroup,
  control: any,
  rule: any
) {
  const formControl: any = formGroup.get(control);
  if (formControl) {
    const validator =
      formControl.validator && formControl.validator(new UntypedFormControl());
    if (validator && validator[rule]) {
      return true;
    }
  }

  return false;
}

export function onFileUploaded(
  formGroup: FormGroup,
  event: any,
  sourceKey: any,
  type: any = 'single'
) {
  if (event.target.files.length > 0) {
    const file = event.target.files[0];
    formGroup.patchValue({
      [sourceKey]: file,
    });
  } else {
    formGroup.patchValue({
      [sourceKey]: null,
    });
  }
}

/**
 * ----------------------------------------
 * JS Functions for Accessing JS Functions
 * ----------------------------------------
 * ----------------------------------------
 */
export function checkIsArray(value: any) {
  // Array.isArray()
  if (Array.isArray(value)) {
    return true;
  } else {
    return false;
  }
}
export function downloadFile(fileUrl: string) {
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = fileUrl.substring(fileUrl.lastIndexOf('/') + 1);
  link.click();
}
